import { getToken, onMessage, deleteToken } from "firebase/messaging";
import { axiosInstance } from "../Utils/AxiosInstance";
import { getMessagingInstance } from "../config/firebaseConfig";
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

class FCMService {
  constructor() {
    this.token = null;
    this.isInitialized = false;
    this.onTokenRefreshCallback = null;
    this.onMessageCallback = null;
    this.messaging = null;

    this.messageHandler = null;
    this.onlineHandler = null;
    this.offlineHandler = null;
    this.controllerChangeHandler = null;
  }

  async getMessaging() {
    if (this.messaging) return this.messaging;

    try {
      this.messaging = await getMessagingInstance();
      if (!this.messaging) {
        throw new Error("Firebase messaging is not supported in this environment");
      }
      return this.messaging;
    } catch (error) {
      console.error("[FCM] Error getting messaging instance:", error);
      throw error;
    }
  }

  /**
   * Register the service worker
   */
  async registerServiceWorker() {
    if ("serviceWorker" in navigator) {
      try {
        const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
          scope: "/",
        });
        // console.log("[FCM] Service Worker registered successfully:", registration);
        return registration;
      } catch (error) {
        console.error("[FCM] Service Worker registration failed:", error);
        throw error;
      }
    } else {
      throw new Error("Service Workers are not supported in this browser");
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!("Notification" in window)) {
      throw new Error("This browser does not support notifications");
    }

    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      // console.log("[FCM] Notification permission granted");
      return true;
    } else if (permission === "denied") {
      console.warn("[FCM] Notification permission denied");
      return false;
    } else {
      console.warn("[FCM] Notification permission default");
      return false;
    }
  }

  /**
   * Get FCM token
   */
  async getFCMToken() {
    try {
      const messaging = await this.getMessaging();

      const registration = await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        // console.log("[FCM] FCM Token obtained:", token);
        this.token = token;
        return token;
      } else {
        console.warn("[FCM] No registration token available");
        return null;
      }
    } catch (error) {
      console.error("[FCM] An error occurred while retrieving token:", error);
      throw error;
    }
  }

  /**
   * Register device token with backend
   */
  async registerDeviceToken(token) {
    try {
      const response = await axiosInstance.post("/Notifications/RegisterDeviceToken", {
        Token: token,
        Platform: "web",
      });

      if (response.data?.IsSuccess) {
        // console.log("[FCM] Device token registered successfully");
        return true;
      } else {
        console.error("[FCM] Failed to register device token:", response.data?.Message);
        return false;
      }
    } catch (error) {
      console.error("[FCM] Error registering device token:", error);
      throw error;
    }
  }

  /**
   * Unregister device token with backend (for logout)
   */
  async unregisterDeviceToken() {
    try {
      if (this.token) {
        const response = await axiosInstance.post("/Notifications/UnregisterDeviceToken", {
          Token: this.token,
          Platform: "web",
        });

        if (response.data?.IsSuccess) {
          // console.log("[FCM] Device token unregistered successfully");
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("[FCM] Error unregistering device token:", error);
      return false;
    }
  }

  /**
   * Handle foreground messages
   */
  setupForegroundMessageHandler(callback) {
    this.onMessageCallback = callback;

    this.getMessaging()
      .then(messaging => {
        onMessage(messaging, payload => {
          // console.log("[FCM] Message received in foreground:", payload);

          if (callback) {
            callback(payload);
          }

          if (payload.notification) {
            const notificationTitle = payload.notification.title || "New Notification";
            const notificationOptions = {
              body: payload.notification.body || "You have a new notification",
              icon: "/favicon.png",
              badge: "/favicon.png",
              tag: payload.data?.notificationId || "notification",
              data: payload.data,
            };

            if ("Notification" in window && Notification.permission === "granted") {
              new Notification(notificationTitle, notificationOptions);
            }
          }
        });
      })
      .catch(error => {
        console.error("[FCM] Error setting up foreground handler:", error);
      });
  }

  /**
   * Initialize FCM service
   */
  async initialize() {
    if (this.isInitialized) {
      // console.log("[FCM] Service already initialized");
      return;
    }

    try {
      if (!("serviceWorker" in navigator)) {
        console.warn("[FCM] Service Workers are not supported");
        this.isInitialized = true;
        return;
      }

      await this.registerServiceWorker();

      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        console.warn("[FCM] Notification permission not granted");
        this.isInitialized = true;
        return;
      }

      const token = await this.getFCMToken();
      if (token) {
        await this.registerDeviceToken(token);
      }

      this.setupTokenRefreshListener();
      this.setupOnlineHandler();
      this.setupServiceWorkerUpdateHandler();

      this.isInitialized = true;
      // console.log("[FCM] FCM service initialized successfully");
    } catch (error) {
      console.error("[FCM] Error initializing FCM service:", error);
      throw error;
    }
  }

  /**
   * Setup token refresh listener
   */
  setupTokenRefreshListener() {
    this.messageHandler = async event => {
      if (event.data && event.data.type === "TOKEN_REFRESH") {
        // console.log("[FCM] Token refresh detected");
        try {
          const newToken = await this.getFCMToken();
          if (newToken && newToken !== this.token) {
            this.token = newToken;
            await this.registerDeviceToken(newToken);

            if (this.onTokenRefreshCallback) {
              this.onTokenRefreshCallback(newToken);
            }
          }
        } catch (error) {
          console.error("[FCM] Error refreshing token:", error);
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", this.messageHandler);
  }

  /**
   * Setup online handler
   */
  setupOnlineHandler() {
    this.onlineHandler = async () => {
      // console.log("[FCM] Device came online. Checking token registration...");
      if (this.token) {
        try {
          await this.registerDeviceToken(this.token);
        } catch (error) {
          console.error("[FCM] Error re-registering token after coming online:", error);
        }
      }
    };

    this.offlineHandler = () => {
      console.warn("[FCM] Device is offline. Token registration will be retried when online.");
    };

    window.addEventListener("online", this.onlineHandler);
    window.addEventListener("offline", this.offlineHandler);
  }

  /**
   * Setup service worker update handler
   */
  setupServiceWorkerUpdateHandler() {
    this.controllerChangeHandler = () => {
      // console.log("[FCM] Service worker updated. Re-initializing...");
      this.isInitialized = false;
    };

    navigator.serviceWorker.addEventListener("controllerchange", this.controllerChangeHandler);
  }

  /**
   * Get current token
   */
  getCurrentToken() {
    return this.token;
  }

  /**
   * Delete token (for logout)
   */
  async deleteToken() {
    try {
      const messaging = await this.getMessaging();

      if (messaging && this.token) {
        await deleteToken(messaging);
        this.token = null;
        this.isInitialized = false;
        // console.log("[FCM] Token deleted successfully");
      }
    } catch (error) {
      console.error("[FCM] Error deleting token:", error);
    }
  }

  /**
   * Clear and stop all notifications (for logout) - COMPREHENSIVE
   */
  async cleanupOnLogout() {
    try {
      // console.log("[FCM] Starting logout cleanup...");

      // 1. Unregister device token from backend
      await this.unregisterDeviceToken();

      // 2. Delete FCM token
      await this.deleteToken();

      // 3. Unregister service worker
      await this.unregisterServiceWorkers();

      // 4. Clear all caches
      await this.clearAllCaches();

      // 5. Clear all notifications
      this.clearAllNotifications();

      // 6. Reset state
      this.resetState();

      // console.log("[FCM] Logout cleanup completed");
      return true;
    } catch (error) {
      console.error("[FCM] Error during logout cleanup:", error);
      return false;
    }
  }

  /**
   * Unregister all service workers
   */
  async unregisterServiceWorkers() {
    if (!("serviceWorker" in navigator)) return;

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        await registration.unregister();
        // console.log(`[FCM] Service Worker unregistered: ${registration.scope}`);
      }
    } catch (error) {
      console.error("[FCM] Error unregistering service workers:", error);
    }
  }

  /**
   * Clear all caches
   */
  async clearAllCaches() {
    try {
      if ("caches" in window) {
        const cacheNames = await caches.keys();

        for (const cacheName of cacheNames) {
          await caches.delete(cacheName);
          // console.log(`[FCM] Cache deleted: ${cacheName}`);
        }
      }
    } catch (error) {
      console.error("[FCM] Error clearing caches:", error);
    }
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications() {
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        // Close service worker notifications
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: "CLEAR_NOTIFICATIONS",
          });
        }
        // console.log("[FCM] Notifications cleared");
      }
    } catch (error) {
      console.error("[FCM] Error clearing notifications:", error);
    }
  }

  /**
   * Reset all state
   */
  resetState() {
    this.token = null;
    this.isInitialized = false;
    this.onTokenRefreshCallback = null;
    this.onMessageCallback = null;
    this.messaging = null;

    // Remove event listeners
    if (this.messageHandler) {
      navigator.serviceWorker.removeEventListener("message", this.messageHandler);
    }

    if (this.onlineHandler) {
      window.removeEventListener("online", this.onlineHandler);
    }

    if (this.offlineHandler) {
      window.removeEventListener("offline", this.offlineHandler);
    }

    if (this.controllerChangeHandler) {
      navigator.serviceWorker.removeEventListener("controllerchange", this.controllerChangeHandler);
    }

    // console.log("[FCM] State reset complete");
  }

  /**
   * Set token refresh callback
   */
  onTokenRefresh(callback) {
    this.onTokenRefreshCallback = callback;
  }
}

// Export singleton instance
const fcmService = new FCMService();
export default fcmService;
